/**
 * Single source of truth for voice_config (API, snake_case) ↔ VoiceConfig
 * (store, camelCase). Replaces 8 hand-rolled copies (usePersonas ×5,
 * useAppInit, ChatContainer persist, restorePersona passthrough).
 * `supportedModels` / `streamingOutputFormat` are frontend-only and are
 * deliberately not mapped in either direction.
 */
import type { VoiceConfig } from '@/stores/personaStore'

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function voiceConfigFromApi(vc: any): VoiceConfig | undefined {
  if (!vc) return undefined
  return {
    provider: vc.provider,
    voiceId: vc.voice_id,
    voiceName: vc.voice_name ?? undefined,
    voicePreviewUrl: vc.voice_preview_url ?? undefined,
    modelId: vc.model_id ?? undefined,
    stability: vc.stability,
    similarityBoost: vc.similarity_boost,
    style: vc.style,
    useSpeakerBoost: vc.use_speaker_boost,
    speed: vc.speed,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    pronunciationRules: vc.pronunciation_rules?.map((r: any) => ({
      stringToReplace: r.string_to_replace,
      type: r.type,
      alias: r.alias,
      phoneme: r.phoneme,
      alphabet: r.alphabet,
    })),
    pronunciationDictionary: vc.pronunciation_dictionary
      ? {
          dictionaryId: vc.pronunciation_dictionary.dictionary_id,
          versionId: vc.pronunciation_dictionary.version_id,
          rulesHash: vc.pronunciation_dictionary.rules_hash,
        }
      : undefined,
    pronunciationDictionaryIds: vc.pronunciation_dictionary_ids ?? undefined,
    sttLanguage: vc.stt_language ?? undefined,
    speechMode: vc.speech_mode ?? undefined,
  }
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function voiceConfigToApi(vc: VoiceConfig | undefined | null): any {
  if (!vc) return null
  return {
    provider: vc.provider,
    voice_id: vc.voiceId,
    voice_name: vc.voiceName,
    voice_preview_url: vc.voicePreviewUrl,
    model_id: vc.modelId,
    stability: vc.stability,
    similarity_boost: vc.similarityBoost,
    style: vc.style,
    use_speaker_boost: vc.useSpeakerBoost,
    speed: vc.speed,
    pronunciation_rules: vc.pronunciationRules?.map((r) => ({
      string_to_replace: r.stringToReplace,
      type: r.type,
      alias: r.alias,
      phoneme: r.phoneme,
      alphabet: r.alphabet,
    })),
    pronunciation_dictionary: vc.pronunciationDictionary
      ? {
          dictionary_id: vc.pronunciationDictionary.dictionaryId,
          version_id: vc.pronunciationDictionary.versionId,
          rules_hash: vc.pronunciationDictionary.rulesHash,
        }
      : undefined,
    pronunciation_dictionary_ids: vc.pronunciationDictionaryIds,
    stt_language: vc.sttLanguage ?? undefined,
    speech_mode: vc.speechMode ?? undefined,
  }
}
